import { type ReactNode, useMemo } from 'react';
import type { RecordDeletionGateway, TenantSummary } from '@forq/contracts';
import type {
  CreateOrderDocumentInput,
  NewCustomerDraft,
  OrderCustomerOption,
  OrderGateway,
  OrderPage,
  OrderRecord,
  OrderSearchInput,
  SaveOrderInput,
  TransitionOrderInput,
} from '@forq/orders-data';
import { OrdersPage as OrdersPageView } from './OrdersPage';

interface StableOrdersPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: OrderGateway;
  readonly deletionGateway: RecordDeletionGateway;
  readonly onSelectedOrderChange?: (order: OrderRecord | null) => void;
  readonly onCreateCustomer?: (draft: NewCustomerDraft) => Promise<OrderCustomerOption>;
  readonly mastersGateway?: import('@forq/masters-data').MastersGateway;
  /** Zusatzinhalt für das Vorgangs-Fenster (Beleg-Aktions-Center + Abrechnung). */
  readonly detailChildren?: ReactNode;
  /** Erhöht sich nach dem Abrechnen im Fenster und lädt die Liste neu (Live-Status). */
  readonly reloadKey?: number;
}

/**
 * Keeps identical list queries referentially stable. The Orders view refreshes a
 * selected row after list changes; stable page objects prevent a selection-only
 * refresh from becoming another network request while mutations still invalidate
 * the cache immediately.
 */
class StableOrdersGateway implements OrderGateway {
  private readonly pages = new Map<string, Promise<OrderPage>>();

  constructor(private readonly source: OrderGateway) {}

  getOverview = (tenantId: string) => this.source.getOverview(tenantId);
  getOrder = (tenantId: string, orderId: string) => this.source.getOrder(tenantId, orderId);
  searchCustomerOptions = (tenantId: string, query?: string) => this.source.searchCustomerOptions(tenantId, query);
  searchMachineOptions = (tenantId: string, query?: string, customerId?: string | null, includeAll?: boolean) => this.source.searchMachineOptions(tenantId, query, customerId, includeAll);
  searchItemOptions = (tenantId: string, query?: string) => this.source.searchItemOptions(tenantId, query);
  createDocument = (input: CreateOrderDocumentInput) => this.source.createDocument(input);
  listDocuments = (tenantId: string, orderId: string) => this.source.listDocuments(tenantId, orderId);

  searchOrders(input: OrderSearchInput): Promise<OrderPage> {
    const key = JSON.stringify(input);
    const existing = this.pages.get(key);
    if (existing) return existing;
    const pending = this.source.searchOrders(input).catch((error: unknown) => {
      this.pages.delete(key);
      throw error;
    });
    this.pages.set(key, pending);
    return pending;
  }

  async saveOrder(input: SaveOrderInput) {
    const result = await this.source.saveOrder(input);
    this.pages.clear();
    return result;
  }

  async transitionOrder(input: TransitionOrderInput) {
    const result = await this.source.transitionOrder(input);
    this.pages.clear();
    return result;
  }
}

export function StableOrdersPage(props: StableOrdersPageProps) {
  const gateway = useMemo(() => new StableOrdersGateway(props.gateway), [props.gateway]);
  return <OrdersPageView {...props} gateway={gateway} />;
}
